import { useAuthSessionRevision } from 'src/auth/sessionEvents';
// Messenger 2.0 (issue #420). Tabbed shell over the new v2 hooks.
//
// Layout: a tab bar across the top selecting All / Job Threads / Broadcasts
// / Support Inbox / Archived, a sidebar of threads (from `Messenger2Sidebar`),
// and the existing message pane (`ChatPaneV2`, which wraps
// `ChatContent` + `BottomBarContent`).
//
// Mounting:
//   - `ChatSocketProvider` (#400) owns the JWT-authenticated socket
//     lifecycle.
//   - `MessengerInner` calls `useChatEventRouter` (#418) so the React Query
//     cache stays in sync with the Sprint 3 vocabulary in real time.
//   - The Support Inbox tab routes to `/messenger/support` (built by #4.6 in
//     `SupportInbox.tsx`) rather than rendering inline; the right pane is
//     hidden when that tab is selected.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  Typography,
  styled,
  useTheme
} from '@mui/material';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import CampaignTwoToneIcon from '@mui/icons-material/CampaignTwoTone';

import BroadcastJobPickerDialog from './BroadcastJobPickerDialog';
import ChatPaneV2 from './ChatPaneV2';
import Messenger2Sidebar from './Messenger2Sidebar';
import { ADMIN_ROLE_NAMES, readSession } from './messengerSession';
import SupportInbox from './SupportInbox';
import TopBarContent from './TopBarContent';

import Scrollbar from 'src/components/Scrollbar';
import { findChat } from 'src/helper/backend_helper';
import { chatKeys } from 'src/queries/chat';
import {
  ChatSocketProvider,
  useChatSocket
} from 'src/realtime/ChatSocketProvider';
import { useChatEventRouter } from 'src/realtime/useChatEventRouter';

import type { Chat } from 'src/models/chat';

const RootWrapper = styled(Box)(
  ({ theme }) => `
       height: calc(100vh - ${theme.header.height});
       display: flex;
       flex-direction: column;
`
);

const Sidebar = styled(Box)(
  ({ theme }) => `
        width: 340px;
        background: ${theme.colors.alpha.white[100]};
        border-right: ${theme.colors.alpha.black[10]} solid 1px;
        overflow-y: auto;
`
);

const ChatWindow = styled(Box)(
  () => `
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        flex: 1;
`
);

const ChatTopBar = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.white[100]};
        border-bottom: ${theme.colors.alpha.black[10]} solid 1px;
        padding: ${theme.spacing(2)};
        align-items: center;
`
);

const DrawerWrapperMobile = styled(Drawer)(
  () => `
    width: 360px;
    flex-shrink: 0;

  & > .MuiPaper-root {
        width: 360px;
        z-index: 3;
  }
`
);

type TabKey = 'all' | 'jobs' | 'broadcasts' | 'support' | 'archived';

const TAB_DEFS: Array<{ key: TabKey; label: string; adminOnly?: boolean }> = [
  { key: 'all', label: 'All' },
  { key: 'jobs', label: 'Job Threads' },
  { key: 'broadcasts', label: 'Broadcasts' },
  { key: 'support', label: 'Support Inbox', adminOnly: true },
  { key: 'archived', label: 'Archived' }
];

type MessengerRouteState = {
  id?: number | string;
  data?: {
    senderId?: number | string;
  };
} | null;

function toPositiveId(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

function MessengerInner() {
  useAuthSessionRevision();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useMemo(() => readSession(), []);
  const isAdmin = !!(
    session?.roleName && ADMIN_ROLE_NAMES.has(session.roleName)
  );

  // Event router: one consumer per session. Mounted here, NOT app-wide.
  useChatEventRouter({ role: session?.roleName });

  const { socket } = useChatSocket();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [broadcastPickerOpen, setBroadcastPickerOpen] = useState(false);
  const [chat, setChat] = useState<Chat | null>(null);
  const lastRequestedUserIdRef = useRef<number | undefined>();
  const routeState = location.state as MessengerRouteState;

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const requestedUserId = useMemo(
    () =>
      toPositiveId(
        routeState?.data?.senderId ??
          routeState?.id ??
          searchParams.get('userId')
      ),
    [routeState, searchParams]
  );
  const rawTab = (searchParams.get('tab') as TabKey | null) ?? 'all';
  const activeTab: TabKey = TAB_DEFS.some((t) => t.key === rawTab)
    ? rawTab
    : 'all';

  const { mutate: openDirectChat } = useMutation(
    (userId: number) => findChat(userId),
    {
      onSuccess: (response: any) => {
        const nextChat = response?.data?.data as Chat | undefined;
        if (!nextChat?.id) return;
        setChat(nextChat);
        queryClient.invalidateQueries(chatKeys.myChatsRoot());
      }
    }
  );

  // Legacy entry points, including contractor rows in bids tables, route here
  // with a target user id in location state. Resolve that user to a chat so
  // the messenger opens the intended direct conversation instead of a default.
  useEffect(() => {
    if (!requestedUserId) return;
    if (lastRequestedUserIdRef.current === requestedUserId) return;
    lastRequestedUserIdRef.current = requestedUserId;
    openDirectChat(requestedUserId);
  }, [openDirectChat, requestedUserId]);

  // Deep-linking: `?chatId=N` opens that thread on mount.
  useEffect(() => {
    const chatIdParam = searchParams.get('chatId');
    if (!chatIdParam) return;
    const chatId = Number(chatIdParam);
    if (!Number.isFinite(chatId)) return;
    if (chat?.id === chatId) return;
    // Minimum stub so the pane mounts; full chat is hydrated by the v2
    // hooks inside the pane.
    setChat({
      id: chatId,
      type: 'DIRECT',
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      participants: []
    } as Chat);
    // Subscribed only to `searchParams` — `chat?.id` would re-fire on every
    // selection and stomp the deep-link param. The router does not have
    // `react-hooks/exhaustive-deps` enabled in lint, so this is documented
    // here for the reader.
  }, [searchParams]);

  const handleTabChange = (_e: unknown, next: TabKey) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', next);
    if (next === 'support') {
      // The support tab routes to its dedicated page so the inbox table can
      // own the full viewport.
      navigate(`/dashboards/messenger/support?${params.toString()}`);
      return;
    }
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  // Resolve the sidebar query filter from the active tab.
  const filter = useMemo<{
    type?: string;
    jobId?: number;
    archived?: boolean;
  }>(() => {
    switch (activeTab) {
      case 'jobs':
        // The controller accepts a single `type=` param. JOB_GROUP threads
        // are the dominant case; JOB_DIRECT threads are surfaced under
        // "All" (per the issue acceptance criteria, jobs tab is
        // `?type=JOB_GROUP,JOB_DIRECT` but the server only accepts a
        // single value — we pin to JOB_GROUP for the primary view and
        // rely on the inbox `All` tab to surface JOB_DIRECT).
        return { type: 'JOB_GROUP', archived: false };
      case 'broadcasts':
        return { type: 'JOB_BROADCAST', archived: false };
      case 'archived':
        return { archived: true };
      case 'all':
      default:
        return { archived: false };
    }
  }, [activeTab]);

  const handleDrawerToggle = () => setMobileOpen((open) => !open);

  return (
    <>
      <Helmet>
        <title>Messenger - Applications</title>
      </Helmet>
      <RootWrapper className="Mui-FixedWrapper">
        <Box
          sx={{
            borderBottom: `1px solid ${theme.colors.alpha.black[10]}`,
            background: theme.colors.alpha.white[100],
            px: 2
          }}
          data-testid="messenger-tabs"
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
          >
            {TAB_DEFS.filter((t) => !t.adminOnly || (isAdmin && session?.roleName === 'SUPER_ADMIN')).map((t) => (
              <Tab
                key={t.key}
                value={t.key}
                label={t.label}
                data-testid={`messenger-tab-${t.key}`}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <DrawerWrapperMobile
            sx={{ display: { lg: 'none', xs: 'inline-block' } }}
            variant="temporary"
            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            open={mobileOpen}
            onClose={handleDrawerToggle}
          >
            <Scrollbar>
              <Box p={2}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  Threads
                </Typography>
                {session && (
                  <Messenger2Sidebar
                    filter={filter}
                    selectedChatId={chat?.id}
                    onSelect={(c) => {
                      setChat(c);
                      setMobileOpen(false);
                    }}
                    currentUserId={session.id}
                  />
                )}
              </Box>
            </Scrollbar>
          </DrawerWrapperMobile>

          <Sidebar
            sx={{ display: { xs: 'none', lg: 'inline-block' } }}
            data-testid="messenger-sidebar"
          >
            <Scrollbar>
              <Box p={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                  }}
                >
                  <Typography variant="h4">Threads</Typography>
                  {activeTab === 'broadcasts' && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CampaignTwoToneIcon />}
                      onClick={() => setBroadcastPickerOpen(true)}
                      data-testid="messenger-new-broadcast"
                    >
                      New broadcast
                    </Button>
                  )}
                </Box>
                {session && (
                  <Messenger2Sidebar
                    filter={filter}
                    selectedChatId={chat?.id}
                    onSelect={(c) => setChat(c)}
                    currentUserId={session.id}
                  />
                )}
              </Box>
            </Scrollbar>
          </Sidebar>

          {chat && socket && (
            <ChatWindow>
              <ChatTopBar sx={{ display: { xs: 'flex', lg: 'inline-block' } }}>
                <IconButton
                  sx={{ display: { lg: 'none', xs: 'flex' }, mr: 2 }}
                  color="primary"
                  onClick={handleDrawerToggle}
                  size="small"
                >
                  <MenuTwoToneIcon />
                </IconButton>
                <TopBarContent chat={chat} />
              </ChatTopBar>
              <Divider />
              <ChatPaneV2
                chat={chat}
                readOnly={chat.type === 'JOB_BROADCAST'}
              />
            </ChatWindow>
          )}
          {!chat && (
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography variant="h5" color="text.secondary">
                Select a conversation
              </Typography>
            </Box>
          )}
        </Box>
      </RootWrapper>

      <BroadcastJobPickerDialog
        open={broadcastPickerOpen}
        onClose={() => setBroadcastPickerOpen(false)}
      />
    </>
  );
}

export default function ApplicationsMessenger() {
  // The provider lives here — NOT app-wide — so login screens never hold an
  // open socket against the gateway (#400 acceptance criteria).
  return (
    <ChatSocketProvider>
      <MessengerInner />
    </ChatSocketProvider>
  );
}

// Re-export the support inbox so the router (`router.tsx`) can mount it
// alongside the messenger without an extra file in the route module.
export { SupportInbox };
